// Modified from platform/packages/search/product-search-dialog/src/extensible/product-router/product/result-provider/faster-search-helpers.ts
import type { ActivityItemEdge } from '@atlassian/search-client/recent-activities/types';
import { highlightMatchedText } from '@atlassian/search-common/highlight-matched-text';

import type { QuickFindResult } from '../../ui/quick-find/types.tsx';
import { normalizeWorkItemKey } from '../../ui/quick-find/utils.ts';

type FilterItems = {
	title: string;
	highlightedTitle?: string;
};

/**
 * TODO: Remove this function because it does too much, making it hard to reuse
 * This will get removed when we no longer use the janky dropdown in the search page
 */
export function filterSearchResultsWithHighlighting<T extends FilterItems>(
	searchResults: T[],
	query: string,
	numberOfResultItems = 3,
	negateTitleHighlight?: boolean,
): T[] {
	const wordsInQuery = query.split(' ');
	const itemScoreMap: {
		[key: string]: number;
	} = {};

	const filteredResults: T[] = searchResults
		.flatMap((result) => {
			// 1st pass
			const doAllWordsMatch = wordsInQuery.reduce((acc, word) => {
				if (!acc) {
					return acc;
				}
				return result.title.toLowerCase().includes(word.toLowerCase());
			}, true);

			if (doAllWordsMatch) {
				// 2nd and 3rd Pass
				const {
					highlightedTitle,
					negatedHighlightedTitle,
					score = 0,
				} = highlightMatchedText(result.title, wordsInQuery);
				itemScoreMap[result.title] = score;

				return [
					{
						...result,
						highlightedTitle: negateTitleHighlight ? negatedHighlightedTitle : highlightedTitle,
					},
				];
			}

			return [];
		})
		.sort((item, secondItem) =>
			item && secondItem ? itemScoreMap[secondItem.title] - itemScoreMap[item.title] : 0,
		)
		.slice(0, numberOfResultItems);

	return filteredResults;
}

/**
 * Copy of the above function, but it doesn't do highlighting or limit the
 * number of results to make it more reusable.
 */
export function filterSearchResults<T extends FilterItems>(searchResults: T[], query: string): T[] {
	const wordsInQuery = query.split(' ');
	const itemScoreMap: {
		[key: string]: number;
	} = {};

	const filteredResults: T[] = searchResults
		.flatMap((result) => {
			// 1st pass
			const doAllWordsMatch = wordsInQuery.reduce((acc, word) => {
				if (!acc) {
					return acc;
				}
				return result.title.toLowerCase().includes(word.toLowerCase());
			}, true);

			if (doAllWordsMatch) {
				// 2nd and 3rd Pass
				const { score = 0 } = highlightMatchedText(result.title, wordsInQuery);
				itemScoreMap[result.title] = score;

				return [
					{
						...result,
					},
				];
			}

			return [];
		})
		.sort((item, secondItem) =>
			item && secondItem ? itemScoreMap[secondItem.title] - itemScoreMap[item.title] : 0,
		);

	return filteredResults;
}

/**
 * Filters recent activities by the given query against each activity's title.
 *
 * The filter runs twice — once with the normalized issue key form of the query (e.g. "AAA34"
 * or "AAA 34" → "aaa-34") and once with the literal query — then merges the
 * results, deduplicating by id. This ensures users find activities whether they
 * type the canonical key format or a variant of it.
 */
export function filterRecentActivities(
	recentActivities: QuickFindResult<ActivityItemEdge>[],
	query: string,
): QuickFindResult<ActivityItemEdge>[] {
	const normalizedQuery = normalizeWorkItemKey(query);

	// Search by both the normalized key and the literal query, deduplicating by id
	const byNormalized =
		normalizedQuery && query !== normalizedQuery
			? filterSearchResults(recentActivities, normalizedQuery)
			: [];
	const byLiteral = filterSearchResults(recentActivities, query);

	const seen = new Set(byNormalized.map((r) => r.id));
	return [...byNormalized, ...byLiteral.filter((r) => !seen.has(r.id))];
}
